import React, { useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Select,
  Text,
  createStyles,
  Loader,
} from "@mantine/core";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { IconArrowLeft, IconChevronDown } from "@tabler/icons-react";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { ogisoURL } from "../../../http";
import { getCookie, setCookie } from "../../../utils";
import { enterProviderView } from "../../../services/skypointProvider";
import {
  PROVIDER_ACCESS_HEARTBEAT_MS,
  endProviderAccessSession,
  heartbeatProviderAccessSession,
} from "../../../utils/providerAccessSession";
import { clearActiveProvider } from "../../../store/reducers/skypointProvider";
import store from "../../../store";

const useStyles = createStyles(() => ({
  container: {
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 20px",
    borderBottom: "1px solid #EAECF0",
    background: "#fff",
    flexShrink: 0,
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 13,
    fontFamily: "Inter",
    fontWeight: 500,
    color: "#344054",
    border: "1px solid #D0D5DD",
    background: "#fff",
    "&:hover": {
      background: "#F9FAFB",
    },
  },
  providerSelect: {
    flex: "0 0 280px",
  },
  viewingLabel: {
    fontSize: 12,
    fontFamily: "Inter",
    fontWeight: 500,
    color: "#667085",
  },
  iframeWrapper: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    zIndex: 10,
  },
}));

const ProviderView = ({ isSidebarOpen }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { activeProvider, skypointProviders } = useSelector(
    (state) => state.skypointProviders
  );
  const [iframeLoading, setIframeLoading] = React.useState(true);

  // Save the Skypoint token on mount so we can restore it when leaving
  const skypointTokenRef = useRef(getCookie("token"));

  // Restore the skypoint token whenever this component unmounts (e.g. sidebar
  // navigation away from provider view). On localhost all ports share cookies,
  // so the iframe's ProviderToken page overwrites the "token" cookie.
  //
  // OD-1524: leaving provider view ends the access session; closing the tab
  // ends it too, and a heartbeat keeps it open while the user stays. Logout
  // ends it from performLogout (before the redirect unloads this page).
  useEffect(() => {
    const heartbeat = setInterval(
      heartbeatProviderAccessSession,
      PROVIDER_ACCESS_HEARTBEAT_MS
    );
    const onPageHide = () => endProviderAccessSession("tab_closed");
    window.addEventListener("pagehide", onPageHide);
    return () => {
      clearInterval(heartbeat);
      window.removeEventListener("pagehide", onPageHide);
      endProviderAccessSession("exit");
      setCookie(skypointTokenRef.current, "token");
      store.dispatch(clearActiveProvider());
    };
  }, []);

  // Pass tokens via URL params to the core app's /provider-token page.
  // This avoids cross-origin cookie issues — the ProviderToken page sets
  // cookies within its own domain context before redirecting to /home.
  const iframeUrl = useMemo(() => {
    if (!activeProvider?.token) return null;
    const params = new URLSearchParams({
      token: activeProvider.token,
      "skypoint-token": skypointTokenRef.current,
    });
    return `${ogisoURL}/provider-token?${params.toString()}`;
  }, [activeProvider?.token]);

  // Build dropdown options from the providers list. External providers are
  // profile-only records with no Ogiso account, so they can never be entered.
  const providerOptions = useMemo(() => {
    if (!skypointProviders) return [];
    return skypointProviders
      .filter((p) => !p.isExternal)
      .map((p) => ({
        value: p.id || p._id,
        label: p.businessName || p.businessProfile?.companyName || p.name || "Unknown Provider",
      }));
  }, [skypointProviders]);

  const handleProviderSwitch = async (providerId) => {
    if (providerId === activeProvider?.id) return;
    const provider = skypointProviders?.find(
      (p) => (p.id || p._id) === providerId
    );
    if (provider?.isExternal) return;
    setIframeLoading(true);
    await enterProviderView(
      providerId,
      provider?.businessName || provider?.businessProfile?.companyName || provider?.name || "Provider"
    );
  };

  const handleBack = () => {
    endProviderAccessSession("exit");
    // Restore the Skypoint admin token (iframe may have overwritten it)
    setCookie(skypointTokenRef.current, "token");
    store.dispatch(clearActiveProvider());
    navigate("/home/provider-skypoint");
  };

  // If no active provider, redirect without rendering
  if (!activeProvider) {
    return <Navigate to="/home/provider-skypoint" replace />;
  }

  return (
    <Box className={classes.container}>
      <AppNavbar
        title="Provider View"
        profile={profile}
        isSidebarOpen={isSidebarOpen}
      />

      {/* Provider toolbar */}
      <Box className={classes.toolbar}>
        <Box className={classes.backBtn} onClick={handleBack}>
          <IconArrowLeft size={16} />
          Back
        </Box>

        <Text className={classes.viewingLabel}>Viewing:</Text>

        <Select
          className={classes.providerSelect}
          value={activeProvider.id}
          data={providerOptions}
          onChange={handleProviderSwitch}
          searchable
          rightSection={<IconChevronDown size={14} color="#667085" />}
          styles={{
            input: {
              fontFamily: "Inter",
              fontSize: 13,
              fontWeight: 600,
              border: "1px solid #D0D5DD",
              borderRadius: 6,
            },
          }}
        />
      </Box>

      {/* Iframe */}
      <Box className={classes.iframeWrapper}>
        {iframeLoading && (
          <Box className={classes.loadingOverlay}>
            <Loader size="md" color="#7F56D9" />
          </Box>
        )}
        {iframeUrl && (
          <iframe
            className={classes.iframe}
            src={iframeUrl}
            title={`Provider: ${activeProvider.name}`}
            onLoad={() => setIframeLoading(false)}
            allow="clipboard-write"
          />
        )}
      </Box>
    </Box>
  );
};

export default ProviderView;
